import { FC, useState } from 'react';
import Menu from '@/components/menu';
import { Box, Stack } from '@mui/material';
import { OrgModel } from '@/lib/models/org.model';
import Tooltip from '../tooltip';
import Icon from '../icon';
import { assumeRole, OrgRole, OrgRoleOpts } from '@/lib/services/auth.service';
import Select from '../form/select';
import Button from '../button';
import { LoadState } from '@/lib/helpers';
import useToast from '@/hooks/use-toast.hook';

interface Props {
  org: OrgModel;
  onClose?: () => void;
  onOpen?: () => void;
}

const AssumeRoleMenu: FC<Props> = ({ org, onOpen, onClose }) => {
  const [role, setRole] = useState<OrgRole>('org:auditor');
  const [loadState, setLoadState] = useState<LoadState>('unloaded');
  const { errorToast } = useToast();

  const { id, name, allowSupportAccess, status } = org;

  const canAssumeRole = !!allowSupportAccess; // @todo must be partner:owner or superuser?
  const isActive = status === 'active';
  const enabled = canAssumeRole && isActive;

  const handleAssumeRole = () => {
    if (!role || loadState !== 'unloaded') {
      return;
    }

    setLoadState('loading');

    assumeRole(id, role).then((response) => {
      if (!response) {
        setLoadState('unloaded');
        errorToast('Role assumption failed');
      }
    });
  };

  const handleChange = (value: string) => {
    setRole(value as OrgRole);
  };

  let tooltip = 'Assume Support Role';
  if (!isActive) {
    tooltip = 'Role Assumption is only available for active organizations';
  } else if (!canAssumeRole) {
    tooltip = 'Role Assumption is disabled for this organization';
  }

  const TriggerEl = (
    <Tooltip title={tooltip}>
      <Icon name="assume-role" disabled={!enabled} />
    </Tooltip>
  );

  const loading = loadState === 'loading';

  return (
    <>
      <Menu
        title={`Assume Support Role: ${name}`}
        menuId="assume-role"
        trigger={TriggerEl}
        onClose={onClose}
        onOpen={onOpen}
        disabled={!enabled}
      >
        <Box width={600}>
          <Stack direction="row" gap={2} alignItems="center">
            <Box width={300}>
              <Select
                label="Support Role"
                name="role"
                value={role}
                options={OrgRoleOpts}
                onChange={handleChange}
                size="small"
              />
            </Box>
            <Box>
              <Button
                label="Assume Role"
                onClick={handleAssumeRole}
                disabled={!role}
                size="small"
                loading={loading}
              />
            </Box>
          </Stack>
        </Box>
      </Menu>
    </>
  );
};

export default AssumeRoleMenu;
